import {get} from "../api";

export async function uploadFile(
  file: File | Blob,
  abortController?: AbortController,
  progressListener?: ({
    percentage,
    size,
    uploaded,
  }: {
    percentage: number;
    size: number;
    uploaded: number;
  }) => void
): Promise<{
  key: string;
  url: string;
  size: number;
  mimetype: string;
  originalname: string;
  width?: number;
  height?: number;
}> {
  const filename = file instanceof File ? file.name : `file-${Date.now()}.jpg`;
  // get pre signed post
  return new Promise(async (resolve, reject) => {
    const { url, fields } = await get(
      "/preSignedUrl",
      {
        filename,
      },
      abortController,
    ).then((data) => data);
    const formData = new FormData();

    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append("Content-Type", file.type);
    formData.append("file", file);
    const result = await new Promise<void>((resolveUpload, rejectUpload) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.upload.onprogress = progressListener
        ? (event) => {
            if (event.loaded && event.total) {
              const percentage = Math.round((event.loaded * 100) / event.total);
              progressListener({
                percentage,
                size: event.total,
                uploaded: event.loaded,
              });
            }
          }
        : null;

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolveUpload();
        } else {
          rejectUpload(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        rejectUpload(new Error("Upload failed due to a network error."));
      };

      if(abortController) {
        abortController.signal.addEventListener("abort", () => {
          xhr.abort();
          rejectUpload(new Error("Upload aborted."));
        });
      }

      xhr.send(formData);
    });

    const uri = `https://creatives.goaffpro.com/${fields.key}`;
    const obj = {
      key: fields.key,
      url: uri,
      size: file.size,
      mimetype: file.type,
      originalname: filename
    };
    if (file.type.startsWith("image")) {
      // once uploaded, get image dimensions
      const img = new Image();
      img.onload = function () {
        resolve({
          //@ts-ignore
          width: this.width,
          //@ts-ignore
          height: this.height,
          ...obj,
        });
      };
      img.onerror = reject;
      img.src = uri;
    } else {
      resolve(obj);
    }
  });
}

async function getVideoThumbnail(file:File, abortController: AbortController): Promise<{
  url: string;
    width: number;
    height: number;
}>{
  try {
    if (file.type.startsWith("video")) {
      const thumbnail = await generateVideoThumbnail(file)
      return uploadFile(new File([thumbnail.blob], 'thumbnail.png'), abortController)
          .then((row) => {
            return {
              url: row.url,
              width: thumbnail.width,
                height: thumbnail.height,
            }
          })
    }
  }catch(e){
    console.log(e)
  }
  return {
    url: '',
    width: 0,
    height: 0,
  }

}

export interface FileType {key: string, url: string, size: number, mimetype: string,
  thumbnail?: string; //creatives.goaffpro.com url available for videos
  originalname: string, width?: number, height?: number}


function xhrUpload(url,
                   formData: FormData,
                   abortController?: AbortController | undefined,

                   progressListener?: ({percentage, size, uploaded}) => void){
  return new Promise<void>((resolveUpload, rejectUpload) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.upload.onprogress = progressListener
        ? (event) => {
          if (event.loaded && event.total) {
            const percentage = Math.round((event.loaded * 100) / event.total);
            progressListener({
              percentage,
              size: event.total,
              uploaded: event.loaded,
            });
          }
        }
        : null;

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolveUpload();
      } else {
        rejectUpload(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      rejectUpload(new Error("Upload failed due to a network error."));
    };

    if(abortController) {
      abortController.signal.addEventListener("abort", () => {
        xhr.abort();
        rejectUpload(new Error("Upload aborted."));
      });
    }

    xhr.send(formData);
  });
}
export async function uploadFileV2(file: File, abortController: AbortController,
                            progressListener?: ({
                              percentage,
                              size,
                              uploaded,
                            }) => void
                            ): Promise<FileType>{
  // get pre signed post
  const bucket = `creatives.goaffpro.com`
  return new Promise(async (resolve, reject)=>{
   const { url, fields } = await get(
        "/preSignedUrl",
        {
          filename:file.name,
        },
        abortController,
    ).then((data) => data);
    const formData = new FormData();
    Object.keys(fields).forEach((key)=>{
      formData.append(key, fields[key])
    })

    formData.append('Content-Type',file.type)
    formData.append("file",file)
    const [, thumbnail] = await Promise.all([
      xhrUpload(url,formData, abortController,progressListener),
      getVideoThumbnail(file, abortController)
    ])
    const uri = `https://creatives.goaffpro.com/${fields.key}`;
    const obj: FileType = {
      key: fields.key,
      url:uri,
      size: file.size,
      mimetype: file.type,
      thumbnail: thumbnail?.url,
      originalname: file.name,
      width: thumbnail?.width,
      height: thumbnail?.height,
    }
    if(file.type.startsWith('image')) {
      // once uploaded, get image dimensions
      const result = await getWidthHeight(uri)
      if(result.width && result.height){
        obj.width = result.width
        obj.height = result.height
      }
    }
    resolve(obj)
  })
}

export async function generateVideoThumbnail(videoFile:File): Promise<{
    blob: Blob;
    width: number;
    height: number;
}> {
  return new Promise((resolve, reject) => {
    // 1. Create a video element to load the file
    const video = document.createElement('video');

    // Create a local URL for the file to load it into the video element
    video.src = URL.createObjectURL(videoFile);
    video.muted = true; // Required for some browsers to autoplay/process
    video.playsInline = true;

    // 2. Seek to 1 second once the metadata is loaded
    video.onloadedmetadata = () => {
      // Ensure the video is at least 1 second long
      video.currentTime = Math.min(1, video.duration);
    };

    // 3. Once the seek operation is complete, draw the frame to a canvas
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if(!ctx) throw new Error('Can\'t find canvas context');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas content to a DataURL (base64 image)
      const dataUrl = canvas.toDataURL('image/jpeg');

      // Cleanup: Revoke the object URL to free memory
      URL.revokeObjectURL(video.src);

      dataURLtoBlob(dataUrl).then((blob)=>{
        resolve({
            blob, width: canvas.width,
            height: canvas.height
        });
      })
    };

    video.onerror = (e) => {
      reject(`Error loading video: ${e}`);
    };
    video.load();
  });
}
async function dataURLtoBlob(dataURL) {
  return fetch(dataURL).then((res)=>res.blob())
}

async function getWidthHeight(imageUri: string): Promise<{width: number, height: number}>{
  return new Promise((resolve, reject)=>{
    const img = new Image();
    img.onload = function() {
      resolve({
        //@ts-ignore
        width: this.width,
        //@ts-ignore
        height: this.height,
      })
    }
    img.onerror = ()=>resolve({
      width: 0,
      height: 0
    })
    img.onerror = reject;
    img.src = imageUri
  })
}
